import React, { useState, useEffect } from "react";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";
import { axiosInstance } from "../../services/axiosInstance";
import { endpoints } from "../../services/endpoint";

const getUtcDate = (d = new Date()) => {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getUtcTime = (d = new Date()) => {
  const h = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${min}`;
};

const EmergencyPage = ({ emergency }) => {
  const [toggleStates, setToggleStates] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [loading, setLoading] = useState(false);

  const customerAWSAccountId = localStorage.getItem("CustomerAWSAccountId");
  const region = localStorage.getItem("Region");
  const instanceId = localStorage.getItem("InstanceId");

  const storedInstance = localStorage.getItem("SelectedInstance");
  let parsedInstance = {};
  if (storedInstance) {
    try { parsedInstance = JSON.parse(storedInstance); } catch (e) {}
  }
  const instanceAlias = parsedInstance.InstanceAlias || localStorage.getItem("instanceName");

  const EncryptedInstanceId = localStorage.getItem("EncryptedId");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isBulkAction, setIsBulkAction] = useState(false);

  const todayUtc = getUtcDate(new Date());

  const formatDateTime = (date, time) => {
    if (!date || !time) return "";
    return new Date(`${date}T${time}:00Z`).toISOString();
  };

  useEffect(() => {
    if (emergency && emergency.length > 0) {
      const initialStates = {};

      emergency.forEach((q) => {
        initialStates[q.arn] = !!q.emergencyStatus;
      });

      setToggleStates(initialStates);
    }
  }, [emergency]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedQueue(null);
    setIsBulkAction(false);
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setLoading(false);
  };

  // toggle handler → enable opens modal, disable calls API directly
  const handleToggle = async (queue) => {
    const isCurrentlyEnabled = !!toggleStates[queue.arn];

    if (isCurrentlyEnabled) {
      // user turning OFF → disable directly
      await disableEmergency(queue);
    } else {
      // user turning ON → open modal
      setSelectedQueue(queue);
      const now = new Date();
      setStartDate(getUtcDate(now));
      setStartTime(getUtcTime(now));
      setEndTime("23:59");
      setOpen(true);
    }
  };

  // ✅ GLOBAL ENABLE BUTTON
  const handleEnableAll = () => {
    if (!emergency.length) {
      showSnackbar("No queues available", "error");
      return;
    }

    const now = new Date();
    setIsBulkAction(true);
    setSelectedQueue(null);
    setStartDate(getUtcDate(now));
    setStartTime(getUtcTime(now));
    setEndTime("00:00");
    setOpen(true);
  };

  const handleDisableAll = async () => {
    if (!emergency.length) {
      showSnackbar("No queues available", "error");
      return;
    }

    setLoading(true);

    const body = {
      Queues: emergency.map(q => q.name),
      EmergencyStatus: false, // ✅ key change
      StartDate: getUtcDate(),
      EndDate: getUtcDate(),
      StartTime: getUtcTime(),
      EndTime: "23:59",
      InstanceAlias: instanceAlias,
      region,
      awsAccount: customerAWSAccountId,
      ID: EncryptedInstanceId
    };

    try {
      const res = await axiosInstance.post(
        endpoints.globalEnable.emergencycloseEnableDisable,
        body
      );

      if (res.status === 200 || res.data?.statusCode === 200) {
        const updated = {};
        emergency.forEach(q => (updated[q.arn] = false));
        setToggleStates(updated);

        showSnackbar("Emergency disabled for all queues!", "success");
      } else {
        showSnackbar("Failed to disable all", "error");
      }
    } catch (err) {
      showSnackbar("API error while disabling", "error");
    }

    setLoading(false);
  };

  // ✅ SUBMIT (single + bulk)
  const handleSubmitEmergency = async () => {
    if (!startDate || !endDate || !startTime || !endTime) {
      showSnackbar("Please fill all fields", "error");
      return;
    }
    const start = new Date(`${startDate}T${startTime}:00Z`);
    const end = new Date(`${endDate}T${endTime}:00Z`);

    if (end <= start) {
      showSnackbar("End date/time must be greater than start date/time", "error");
      return;
    }

    setLoading(true);

    let body;

    if (isBulkAction) {
      body = {
        Queues: emergency.map(q => q.name),
        EmergencyStatus: true,
        StartDate: startDate,
        EndDate: endDate,
        StartTime: startTime,
        EndTime: endTime,
        InstanceAlias: instanceAlias,
        region,
        awsAccount: customerAWSAccountId,
        ID: EncryptedInstanceId
      };
    } else {
      body = {
        Queue: selectedQueue.name,
        EmergencyStatus: true,
        StartDate: startDate,
        EndDate: endDate,
        StartTime: startTime,
        EndTime: endTime,
        InstanceAlias: instanceAlias,
        region,
        awsAccount: customerAWSAccountId,
        ID: EncryptedInstanceId
      };
    }

    try {
      const res = await axiosInstance.post(
        endpoints.globalEnable.emergencycloseEnableDisable,
        body
      );

      if (res.status === 200 || res.data?.statusCode === 200) {
        if (isBulkAction) {
          const updated = {};
          emergency.forEach(q => (updated[q.arn] = true));
          setToggleStates(updated);
          showSnackbar("Emergency enabled for all queues!", "success");
        } else {
          setToggleStates(prev => ({
            ...prev,
            [selectedQueue.arn]: true
          }));
          showSnackbar("Emergency enabled successfully!", "success");
        }

        handleClose();
      } else {
        showSnackbar("Failed to submit", "error");
      }
    } catch (err) {
      showSnackbar("API error", "error");
    }

    setLoading(false);
  };

  // ✅ DISABLE
  const disableEmergency = async (queue) => {
    const body = {
      Queue: queue.name,
      EmergencyStatus: false,
      StartDate: getUtcDate(),
      EndDate: getUtcDate(),
      StartTime: getUtcTime(),
      EndTime: "23:59",
      InstanceAlias: instanceAlias,
      region,
      awsAccount: customerAWSAccountId,
      ID: EncryptedInstanceId
    };

    try {
      const res = await axiosInstance.post(
        endpoints.globalEnable.emergencycloseEnableDisable,
        body
      );

      if (res.status === 200 || res.data?.statusCode === 200) {
        setToggleStates(prev => ({
          ...prev,
          [queue.arn]: false
        }));
        showSnackbar(`Emergency disabled for ${queue.name}`, "success");
      }
    } catch {
      showSnackbar("Disable failed", "error");
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px"
      }}>
        <Typography
          variant="h6"
          style={{ textAlign: "center", flex: 1 }}
        >
          Emergency Close - Queue List
        </Typography>

        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            variant="contained"
            onClick={handleEnableAll}
            size="small"
            sx={{
              backgroundColor: "#ef4444",
              fontSize: "11px",
              padding: "3px 8px",
              minWidth: "110px",
              "&:hover": {
                backgroundColor: "#dc2626"
              }
            }}
          >
            Enable Emergency
          </Button>
          <Button
            variant="contained"
            onClick={handleDisableAll}
            size="small"
            sx={{
              backgroundColor: "#6b7280",
              fontSize: "11px",
              padding: "3px 8px",
              minWidth: "110px"
            }}
          >
            Disable Emergency
          </Button>
        </div>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Queue Name</strong></TableCell>
            <TableCell><strong>Description</strong></TableCell>
            <TableCell><strong>Emergency</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {emergency.map((queue, index) => (
            <TableRow key={index}>
              <TableCell>{queue.name}</TableCell>
              <TableCell>{queue.description}</TableCell>
              <TableCell>
                <Switch
                  checked={!!toggleStates[queue.arn]}
                  onChange={() => handleToggle(queue)}
                  color="primary"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Emergency Modal */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Emergency Close Schedule
          {isBulkAction
            ? " - All Queues"
            : selectedQueue && ` - ${selectedQueue.name}`}
        </DialogTitle>
        <DialogContent>
          <div className="addusers-user-form-fields">
            <TextField
              label="Start Date (UTC)"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: todayUtc }}
              required
            />
            <TextField
              label="End Date"
              type="date"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || todayUtc }}
              required
            />
            <TextField
              label="Start Time (UTC)"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="End Time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </div>
        </DialogContent>
        <DialogActions>
          {/* Cancel Button */}
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}  // disable while submitting
          >
            Cancel
          </Button>

          {/* Submit Button */}
          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            onClick={handleSubmitEmergency} // call the function directly
          >
            {loading ? "Saving..." : "Save"}
          </Button>

        </DialogActions>

      </Dialog>
    </Paper>
  );
};

export default EmergencyPage;
